import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/auth/AuthContext';
import api from '@/shared/api/client';
import { Tabs, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import Breadcrumb from '@/shared/components/Breadcrumb';
import {
  RefreshCw, Search, Filter, RotateCcw, Layers
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Checkbox } from '@/shared/ui/checkbox';
import { toast } from 'sonner';
import { LABEL as TYPE_LABEL } from '@/shared/constants/assetTypes';
import { MANDAT_STATUS_CHIP } from '@/shared/constants/statuses';
import useBulkSelection from '@/shared/hooks/useBulkSelection';
import useBulkAction from '@/shared/hooks/useBulkAction';
import useCommandConfirmation from '@/shared/hooks/useCommandConfirmation';
import useRafraichissementTempsReel from '@/shared/hooks/useRafraichissementTempsReel';
import IndicateurFlux from '@/shared/components/IndicateurFlux';
import BulkEditDialog from '@/shared/components/BulkEditDialog';
import BulkResultDialog from '@/shared/components/BulkResultDialog';
import ConfirmDialog from '@/shared/components/ConfirmDialog';
import PilotageResultDialog from '@/shared/components/PilotageResultDialog';
import AssetDetailPanel from '@/features/assets/components/AssetDetailPanel';
import AssetMonitoringCard from '@/features/assets/components/AssetMonitoringCard';
import ASSET_BULK_ACTIONS from '@/features/assets/bulkActions';
import { LIBELLE_IDENTIFIANT } from '@/shared/constants/assetTypes';
import { executeAssetAction, fetchPilotageActions } from '@/shared/api/pilotage';
import { SANS_CONTACT, etatJoignabilite } from '@/shared/utils/liveness';
import { TAILLE_PAGE, lireBornes } from '@/shared/api/troncature';
import BandeauTroncature from '@/shared/components/BandeauTroncature';
import BarrePagination from '@/shared/components/BarrePagination';
import OptionsAgregateurs from '@/shared/components/OptionsAgregateurs';
import useCapacites from '@/shared/auth/useCapacites';
import { CAP } from '@/shared/auth/capacites';

export default function MonitoringPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { possede } = useCapacites();
  const isAdmin = user?.role === 'admin';
  const [piloting, setPiloting] = useState(false);
  const [batteryActions, setBatteryActions] = useState({});
  const [lastCommand, setLastCommand] = useState(null);
  const [pilotageResult, setPilotageResult] = useState(null);
  const [confirmPending, setConfirmPending] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [history, setHistory] = useState([]);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filtersData, setFiltersData] = useState({ marques: [], regions: [], installateurs: [] });
  // `bornes` porte le TOTAL rendu par le serveur. L'ecran affichait `assets.length`,
  // c'est-a-dire le compte de la fenetre de lecture : « 500 resultats » pouvait
  // vouloir dire 500, ou 6 812.
  const [bornes, setBornes] = useState({ total: null, page: 1, taille: TAILLE_PAGE, tronque: false, borne: null });
  const [page, setPage] = useState(1);

  // Filters state
  const [marqueFilter, setMarqueFilter] = useState('');
  const [connexionFilter, setConnexionFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [installateurFilter, setInstallateurFilter] = useState('');
  const [aggregateurFilter, setAggregateurFilter] = useState('');
  // « Quels dossiers attendent encore leur mandat ? » — la question que David pose dès
  // que ces actifs deviennent visibles. Même famille que la liste « en attente de
  // matériel » du parcours installateur : un filtre sur l'écran existant, pas un
  // second écran à tenir à jour.
  const [mandatFilter, setMandatFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [editAsset, setEditAsset] = useState(null);
  const [editForm, setEditForm] = useState({});

  // --- Actions en masse (admin uniquement) ---
  const bulkSel = useBulkSelection(assets);
  const bulk = useBulkAction(() => { fetchAssets(); bulkSel.clear(); });
  const [bulkOpen, setBulkOpen] = useState(false);
  const confirmation = useCommandConfirmation();

  const assetLabel = (id) => {
    const a = assets.find(x => x.id === id);
    return a ? `${TYPE_LABEL[a.type] || a.type} — ${a.client_nom}` : id;
  };

  useEffect(() => {
    api.get('/monitoring/filters').then(r => setFiltersData(r.data)).catch(() => {});
  }, []);

  // Les commandes batterie viennent du catalogue serveur, comme sur l'écran de Pilotage :
  // une liste en dur ici est exactement ce qui avait fait diverger les deux écrans.
  useEffect(() => {
    if (!isAdmin) return;
    fetchPilotageActions().then(m => setBatteryActions(m.Batterie || {})).catch(() => {});
  }, [isAdmin]);

  const fetchAssets = useCallback(async () => {
    try {
      const params = {};
      if (activeTab !== 'all') params.type = activeTab;
      if (marqueFilter) params.marque = marqueFilter;
      // Le filtre serveur ne connaît que connecté/déconnecté. « Aucun contact enregistré »
      // est donc trié ici, sur `etat_connexion` : lui envoyer cette valeur ne renverrait rien.
      if (connexionFilter && connexionFilter !== SANS_CONTACT) {
        params.statut_connexion = connexionFilter;
      }
      if (regionFilter) params.region = regionFilter;
      if (installateurFilter) params.installateur = installateurFilter;
      if (aggregateurFilter) params.aggregateur = aggregateurFilter;
      if (mandatFilter) params.statut_mandat = mandatFilter;
      if (searchQuery) params.search = searchQuery;
      params.page = page;
      params.taille = TAILLE_PAGE;
      const res = await api.get('/monitoring/assets', { params });
      setBornes(lireBornes(res));
      const liste = connexionFilter === SANS_CONTACT
        ? res.data.filter(a => etatJoignabilite(a) === SANS_CONTACT)
        : res.data;
      setAssets(liste);
      // garde le détail ouvert synchronisé avec la donnée fraîche (télémétrie live)
      setSelectedAsset(prev => (prev ? (liste.find(a => a.id === prev.id) || prev) : prev));
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [activeTab, marqueFilter, connexionFilter, regionFilter,
      installateurFilter, aggregateurFilter, mandatFilter, searchQuery, page]);

  // Un changement de filtre ramene a la premiere page. Sans cela, filtrer depuis
  // la page 7 sur un critere qui ne rend que trois actifs afficherait une liste
  // vide, et rien a l'ecran n'en donnerait la raison.
  useEffect(() => { setPage(1); }, [activeTab, marqueFilter, connexionFilter,
    regionFilter, installateurFilter, aggregateurFilter, mandatFilter, searchQuery]);

  useEffect(() => { fetchAssets(); }, [fetchAssets, selectedAsset?.id]);

  // TEMPS RÉEL (retour de David du 28/09 : « 90 s c'est trop long »). Le serveur annonce
  // les actifs qui changent ; on ne relit que si l'un d'eux est affiché. Sans flux, on
  // retombe à 10 s, et à 4 s quand une batterie est ouverte, comme avant.
  const idsAffiches = useMemo(() => new Set(assets.map(a => a.id)), [assets]);
  const { etat: etatFlux } = useRafraichissementTempsReel({
    relire: fetchAssets,
    ids: idsAffiches,
    actif: autoRefresh,
    rythmeRapideMs: selectedAsset?.type === 'Batterie' ? 4000 : null,
  });

  const fetchHistory = async (assetId) => {
    try { const r = await api.get(`/monitoring/assets/${assetId}/history`); setHistory(r.data); }
    catch (e) { console.error(e); }
  };

  /**
   * Envoie une commande batterie et rend compte dans une popup.
   *
   * Passe par `/pilotage/asset/{id}/action`, le même chemin que l'écran de Pilotage :
   * même vocabulaire produit, même journal, même validation de plage. La surface
   * historique `/pilotage/assets/{id}/battery` reste en place pour la compatibilité,
   * mais l'interface ne l'appelle plus.
   */
  const handleBatteryCommand = async (code, watts) => {
    if (!selectedAsset) return;
    const asset = selectedAsset;
    const def = batteryActions[code];
    setPiloting(true);
    const base = {
      asset,
      batteryAction: def?.battery_action,
      actionLabel: def?.label || code,
      value: watts,
      valueUnit: 'W',
      at: new Date().toLocaleString('fr-FR'),
    };
    try {
      const res = await executeAssetAction(asset.id, code, watts ?? null);
      setLastCommand({ action: def?.label || code, watts, ts: Date.now() });
      setPilotageResult({ ...res, ...base });
      fetchAssets();
      if (res.success && !res.simulated) {
        await confirmation.verify(asset.id, def?.battery_action);
      } else {
        confirmation.reset();
      }
      fetchAssets();
    } catch (err) {
      const detail = err.response?.data?.detail;
      confirmation.reset();
      setPilotageResult({
        ...base,
        success: false,
        error: typeof detail === 'string' ? detail : 'Échec de la commande',
      });
    }
    setPiloting(false);
  };

  const closePilotageResult = () => { confirmation.cancel(); setPilotageResult(null); };

  const selectAsset = (asset) => { setSelectedAsset(asset); fetchHistory(asset.id); };

  const openEditAsset = (asset, e) => {
    e.stopPropagation();
    setEditForm({ marque: asset.marque, modele: asset.modele, puissance_kw: asset.puissance_kw, identifiant_equipement: asset.identifiant_equipement });
    setEditAsset(asset);
  };
  const handleEditAsset = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/assets/${editAsset.id}`, editForm);
      // LE VERDICT SE MONTRE, IL NE SE DEVINE PAS.
      //
      // Le serveur rejoue la vérification d'enrôlement dès que l'identifiant change,
      // et rend son verdict sous `enrolement`. C'est la seule question que se pose
      // celui qui corrige un numéro : est-ce que la machine répond, maintenant ?
      // Sans cet affichage, il enregistre et repart sans savoir.
      if (data?.enrolement) {
        const v = data.enrolement;
        (v.vivant ? toast.success : toast.warning)(
          `Actif mis à jour — ${v.message}`, { duration: v.vivant ? 5000 : 12000 });
      } else {
        toast.success('Actif mis à jour');
      }
      setEditAsset(null);
      fetchAssets();
    } catch (err) {
      // LE MESSAGE DU SERVEUR, JAMAIS UN « Erreur » MUET.
      //
      // Le refus qui compte ici est le 409 « cet identifiant est déjà rattaché à un
      // autre appareil ». Remplacé par le mot « Erreur », il devenait indéchiffrable :
      // l'utilisateur relisait un numéro parfaitement valide sans comprendre.
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Erreur lors de la mise à jour de l'actif",
                  { duration: 12000 });
    }
  };
  const askDeleteAsset = (asset, e) => {
    e.stopPropagation();
    setConfirmPending({
      title: "Supprimer l'actif",
      targetLabel: 'Actif ciblé',
      target: `${asset.marque} ${asset.modele} — ${asset.client_nom}`,
      warning: "Suppression définitive : l'actif et son historique de monitoring "
        + "disparaissent, aucune restauration n'est possible depuis l'interface.",
      confirmLabel: 'Supprimer',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await api.delete(`/assets/${asset.id}`);
          toast.success('Actif supprimé');
          if (selectedAsset?.id === asset.id) setSelectedAsset(null);
          fetchAssets();
        } catch { toast.error('Erreur'); }
      },
    });
  };

  // --- Actions en masse ---
  // Le catalogue vit dans `features/assets/bulkActions.js`. Ici on ne fait que lui
  // donner les identifiants qu'il a lui-même délimités via son `scope`.

  const runBulk = async (code, value, def) => {
    const ids = def.scope ? def.scope(bulkSel.selectedItems).ids : bulkSel.ids;
    setBulkOpen(false);
    await bulk.run(() => def.run(ids, value), {
      alwaysReport: def.alwaysReport,
      success: r => `${r.ok} actif${r.ok > 1 ? 's' : ''} traité${r.ok > 1 ? 's' : ''}`,
    });
  };

  const clearFilters = () => {
    setMarqueFilter(''); setConnexionFilter(''); setRegionFilter('');
    setInstallateurFilter(''); setAggregateurFilter(''); setMandatFilter('');
    setSearchQuery(''); setActiveTab('all');
  };

  const hasFilters = marqueFilter || connexionFilter || regionFilter || installateurFilter || aggregateurFilter || mandatFilter || searchQuery || activeTab !== 'all';

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8 lg:p-10 space-y-5" data-testid="monitoring-page">
      <div className="flex items-center justify-between">
        <div>
          <Breadcrumb items={[{ label: 'Monitoring' }]} />
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Monitoring temps reel</h1>
          <p className="text-sm text-neutral-400 mt-0.5">Lecture seule &mdash; Pilotage agrégateur</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && bulkSel.count > 0 && (
            <Button size="sm" onClick={() => setBulkOpen(true)} data-testid="open-bulk-btn">
              <Layers className="w-3.5 h-3.5 mr-1" /> Édition groupée ({bulkSel.count})
            </Button>
          )}
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium border ${
              autoRefresh ? 'bg-neutral-900 border-neutral-900 text-white' : 'bg-white border-neutral-200 text-neutral-500'
            }`}
            data-testid="auto-refresh-toggle"
          >
            <RefreshCw className={`w-3 h-3 ${autoRefresh ? 'animate-spin' : ''}`} style={autoRefresh ? {animationDuration:'3s'} : {}} />
            Auto-refresh
          </button>
          <IndicateurFlux etat={etatFlux} actif={autoRefresh} />
        </div>
      </div>

      {/* Type tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="monitoring-tab-all">Tous ({bornes.total ?? assets.length})</TabsTrigger>
          <TabsTrigger value="PAC" data-testid="monitoring-tab-pac">PAC</TabsTrigger>
          <TabsTrigger value="PV" data-testid="monitoring-tab-pv">PV</TabsTrigger>
          <TabsTrigger value="Ballon" data-testid="monitoring-tab-ballon">ECS</TabsTrigger>
          <TabsTrigger value="Batterie" data-testid="monitoring-tab-batterie">Batterie</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters bar */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-neutral-200/80 bg-white">
        <Filter className="w-4 h-4 text-neutral-400 flex-shrink-0" />

        <div className="relative flex-1 min-w-[160px] max-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
          <Input
            className="pl-8 h-8 text-[12px]"
            placeholder="Rechercher client..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="monitoring-search"
          />
        </div>

        <Select value={marqueFilter || 'all'} onValueChange={v => setMarqueFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-[140px] text-[12px]" data-testid="filter-marque">
            <SelectValue placeholder="Marque" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes marques</SelectItem>
            {filtersData.marques.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={connexionFilter || 'all'} onValueChange={v => setConnexionFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-[140px] text-[12px]" data-testid="filter-connexion-monitoring">
            <SelectValue placeholder="Connexion" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            <SelectItem value="connecte">Connecté</SelectItem>
            <SelectItem value="deconnecte">Déconnecté</SelectItem>
            {/* Une machine déclarée dont aucun contact n'est enregistré n'est pas une
                machine tombée : elle appelle un appel au fournisseur, pas au client. */}
            <SelectItem value={SANS_CONTACT}>Aucun contact enregistré</SelectItem>
          </SelectContent>
        </Select>

        <Select value={regionFilter || 'all'} onValueChange={v => setRegionFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-[160px] text-[12px]" data-testid="filter-region-monitoring">
            <SelectValue placeholder="Region" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes regions</SelectItem>
            {filtersData.regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={installateurFilter || 'all'} onValueChange={v => setInstallateurFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-[150px] text-[12px]" data-testid="filter-installateur">
            <SelectValue placeholder="Installateur" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous installateurs</SelectItem>
            {filtersData.installateurs.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={aggregateurFilter || 'all'} onValueChange={v => setAggregateurFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-[140px] text-[12px]" data-testid="filter-aggregateur-monitoring">
            <SelectValue placeholder="Agrégateur" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous agrégateurs</SelectItem>
            <OptionsAgregateurs />
          </SelectContent>
        </Select>

        {/* Les valeurs viennent du serveur : proposer un statut absent du parc rendrait
            une liste vide et ferait douter du filtre lui-meme. */}
        <Select value={mandatFilter || 'all'} onValueChange={v => setMandatFilter(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-[150px] text-[12px]" data-testid="filter-mandat-monitoring">
            <SelectValue placeholder="Mandat" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous mandats</SelectItem>
            {(filtersData.statuts_mandat || []).map(st => (
              <SelectItem key={st} value={st}>{MANDAT_STATUS_CHIP[st]?.label || st}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="ml-auto text-[11px] text-neutral-500 font-medium" data-testid="monitoring-results-count">
          {(bornes.total ?? assets.length).toLocaleString('fr-FR')} résultat{(bornes.total ?? assets.length) > 1 ? 's' : ''}
        </span>

        {hasFilters && (
          <button onClick={clearFilters} className="flex items-center gap-1 text-[11px] text-neutral-600 hover:text-neutral-900 font-medium px-2 py-1 rounded hover:bg-neutral-100" data-testid="clear-filters-btn">
            <RotateCcw className="w-3 h-3" /> Réinitialiser les filtres
          </button>
        )}
      </div>

      <BandeauTroncature etat={bornes} quoi="actifs" />

      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className={`${selectedAsset ? 'lg:col-span-2' : 'lg:col-span-3'} space-y-2`}>
          {assets.length === 0 ? (
            <div className="p-12 text-center text-neutral-400 border border-dashed border-neutral-200 rounded-lg">
              Aucun actif correspond aux filtres
            </div>
          ) : (
            <>
            {isAdmin && (
              <div className="flex items-center gap-3 px-3 py-2 text-[11px] uppercase tracking-wider text-neutral-400 font-medium">
                <Checkbox
                  checked={bulkSel.allSelected}
                  onCheckedChange={bulkSel.toggleAll}
                  data-testid="select-all-assets"
                />
                <span>Tout sélectionner</span>
                {bulkSel.count > 0 && (
                  <span className="normal-case tracking-normal text-neutral-500">
                    · {bulkSel.count} choisi{bulkSel.count > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {assets.map(asset => (
                <AssetMonitoringCard
                  key={asset.id}
                  asset={asset}
                  isAdmin={isAdmin}
                  isSelected={selectedAsset?.id === asset.id}
                  isChecked={bulkSel.isSelected(asset.id)}
                  onToggleCheck={() => bulkSel.toggle(asset.id)}
                  onSelect={() => selectAsset(asset)}
                  onEdit={e => openEditAsset(asset, e)}
                  onDelete={e => askDeleteAsset(asset, e)}
                />
              ))}
            </div>
            <BarrePagination etat={bornes} onPage={setPage} quoi="actifs" busy={loading} />
            </>
          )}
        </div>

        {/* Colonne de détail — extraite dans `AssetDetailPanel` : cette page dépassait
            déjà les 500 lignes, et c'est la fiche qui devait s'enrichir. */}
        {selectedAsset && (
          <AssetDetailPanel
            asset={selectedAsset}
            isAdmin={isAdmin}
            batteryActions={batteryActions}
            busy={piloting}
            lastCommand={lastCommand}
            onBatteryCommand={handleBatteryCommand}
            history={history}
            onOpenClient={() => navigate(`/clients/${selectedAsset.client_id}`)}
            peutApparierPv={possede(CAP.INTEGRATIONS_MANAGE)}
            onApparie={fetchAssets}
          />
        )}
      </div>

      {/* Edit Asset Dialog */}
      <Dialog open={!!editAsset} onOpenChange={v => { if (!v) setEditAsset(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Modifier l'actif</DialogTitle></DialogHeader>
          <form onSubmit={handleEditAsset} className="space-y-3">
            <div><Label className="text-xs">Marque</Label><Input className="h-9" value={editForm.marque || ''} onChange={e => setEditForm(f => ({ ...f, marque: e.target.value }))} data-testid="monitoring-edit-marque" /></div>
            <div><Label className="text-xs">Modele</Label><Input className="h-9" value={editForm.modele || ''} onChange={e => setEditForm(f => ({ ...f, modele: e.target.value }))} data-testid="monitoring-edit-modele" /></div>
            <div><Label className="text-xs">Puissance (kW)</Label><Input className="h-9" type="number" step="0.1" value={editForm.puissance_kw || ''} onChange={e => setEditForm(f => ({ ...f, puissance_kw: parseFloat(e.target.value) || 0 }))} data-testid="monitoring-edit-puissance" /></div>
            {/* Le libellé NOMME le numéro, il ne le désigne plus vaguement. Selon le
                type, ce champ ne porte pas la même chose, et « ID Équipement » ne
                permettait pas de savoir lequel recopier — c'est très probablement ce
                qui a produit le `28045340019` à onze chiffres de la pré-production,
                là où la machine publie sous `280425340019`. */}
            <div><Label className="text-xs">{LIBELLE_IDENTIFIANT[editAsset?.type] || LIBELLE_IDENTIFIANT.defaut}</Label><Input className="h-9" value={editForm.identifiant_equipement || ''} onChange={e => setEditForm(f => ({ ...f, identifiant_equipement: e.target.value }))} data-testid="monitoring-edit-equip-id" /></div>
            <Button type="submit" className="w-full" data-testid="monitoring-submit-edit-btn">Enregistrer</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Actions en masse — admin uniquement.
          Le catalogue et la portée de chaque action viennent de `bulkActions.js` : cet
          écran ne décide plus, action par action, à qui l'ordre s'applique. */}
      <BulkEditDialog
        open={bulkOpen}
        title="Édition groupée"
        noun="actif"
        count={bulkSel.count}
        actions={ASSET_BULK_ACTIONS}
        selectedItems={bulkSel.selectedItems}
        busy={bulk.busy}
        onCancel={() => setBulkOpen(false)}
        onSubmit={runBulk}
      />

      <BulkResultDialog
        report={bulk.report} onClose={bulk.clearReport}
        title="Résultat de l'action en masse" resolveLabel={assetLabel}
      />

      <PilotageResultDialog
        result={pilotageResult}
        confirm={confirmation}
        onClose={closePilotageResult}
      />

      <ConfirmDialog
        pending={confirmPending}
        onCancel={() => setConfirmPending(null)}
        onConfirm={async () => {
          const action = confirmPending?.onConfirm;
          setConfirmPending(null);
          await action?.();
        }}
      />
    </div>
  );
}
